import React, { useState } from 'react';
import { X, ArrowRight, MousePointer2, GitMerge, AlertTriangle, Play } from 'lucide-react';

interface TutorialOverlayProps {
  onComplete: () => void;
}

export default function TutorialOverlay({ onComplete }: TutorialOverlayProps) {
  const [step, setStep] = useState(0);

  const steps = [
    {
      title: "Welcome to Nebula Merge",
      content: "Relax, drop, and merge celestial bodies in this cosmic puzzle.",
      icon: <div className="text-5xl animate-pulse">🌌</div>
    },
    {
      title: "How to Play",
      content: "Move your mouse or finger left & right to aim. Release to drop the orb.",
      icon: <MousePointer2 size={48} className="text-cyan-400" />
    },
    {
      title: "Merge & Grow",
      content: "Match identical bodies to merge them into larger stars. Can you reach the Black Hole?",
      icon: <GitMerge size={48} className="text-purple-400" />
    },
    {
      title: "Danger Zone",
      content: "Don't let the orbs stack up too high! If they stay above the dotted line, the simulation ends.",
      icon: <AlertTriangle size={48} className="text-red-400" />
    }
  ];

  const handleNext = () => {
    if (step < steps.length - 1) {
      setStep(step + 1);
    } else {
      onComplete();
    }
  };

  return (
    <div className="absolute inset-0 z-[100] bg-slate-950/90 backdrop-blur-md flex items-center justify-center p-6 animate-in fade-in duration-300">
        <div className="w-full max-w-[320px] bg-slate-900/90 border border-white/10 rounded-2xl shadow-2xl p-6 relative overflow-hidden ring-1 ring-white/5 flex flex-col items-center">
            
            {/* Background Glow */}
            <div className="absolute top-0 right-0 p-16 bg-indigo-500/20 blur-[60px] rounded-full pointer-events-none -mt-10 -mr-10" />
            <div className="absolute bottom-0 left-0 p-16 bg-cyan-500/10 blur-[60px] rounded-full pointer-events-none -mb-10 -ml-10" />

            {/* Skip Button (Icon) */}
            <button 
                onClick={onComplete}
                className="absolute top-3 right-3 p-1.5 rounded-full text-slate-500 hover:text-white hover:bg-white/10 transition-colors z-10"
                aria-label="Skip Tutorial"
            >
                <X size={18} />
            </button>

            <div className="flex flex-col items-center text-center relative z-10 pt-2 w-full">
                {/* Icon Container */}
                <div className="mb-6 w-20 h-20 bg-slate-800/80 rounded-2xl shadow-[inset_0_2px_10px_rgba(0,0,0,0.3)] border border-white/5 flex items-center justify-center">
                    {steps[step].icon}
                </div>
                
                <h2 className="text-xl font-bold text-white mb-3 tracking-wide">{steps[step].title}</h2>
                <p className="text-slate-300 text-sm leading-relaxed mb-8 min-h-[4.5em]">
                    {steps[step].content}
                </p>

                {/* Progress Indicators */}
                <div className="flex items-center justify-center gap-1.5 mb-6 w-full">
                    {steps.map((_, i) => (
                        <div 
                            key={i} 
                            className={`h-1.5 rounded-full transition-all duration-300 ${
                                i === step ? 'w-6 bg-indigo-500 shadow-[0_0_8px_rgba(99,102,241,0.6)]' : 'w-1.5 bg-slate-700'
                            }`} 
                        />
                    ))}
                </div>

                {/* Main Action Button */}
                <button 
                    onClick={handleNext}
                    className="w-full flex items-center justify-center gap-2 py-3 bg-white text-slate-950 rounded-xl font-bold hover:bg-indigo-50 active:scale-[0.98] transition-all shadow-[0_4px_20px_rgba(255,255,255,0.15)] group mb-4"
                >
                    {step === steps.length - 1 ? (
                        <>Start Game <Play size={16} className="fill-current" /></>
                    ) : (
                        <>Next <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" /></>
                    )}
                </button>

                {/* Text Skip Button */}
                {step < steps.length - 1 && (
                    <button 
                        onClick={onComplete}
                        className="text-xs text-slate-500 hover:text-white font-medium tracking-wide uppercase px-4 py-2 hover:bg-white/5 rounded-lg transition-colors"
                    >
                        Skip Tutorial
                    </button>
                )}
            </div>
        </div>
    </div>
  );
}